import { Children, createContext, type CSSProperties, type ReactNode, useContext, useMemo } from 'react'
import type {
	Background,
	BorderRadiusCorners,
	FontFamilyMode,
	FontFamilyProvider,
	Spacing,
	SubstackAlignItems,
	SubstackChildSizing,
	SubstackDirection,
	SubstackJustifyContent,
	SubstackWidthSizing,
} from '../data/v1'
import { borderRadiusStyle } from '../lib/border-radius'
import { useFontFamilyStyle } from '../lib/use-font-family-style'
import { BackgroundFill } from './background-fill'

type ParentLayout = { direction: SubstackDirection; childSizing: SubstackChildSizing }

// The CSS that fills the parent's width depends on the parent's layout: growing along a
// flex row, stretching across a flex column, justifying inside an equal-split grid column.
// Content layers are flex columns, hence the default.
const ParentLayoutContext = createContext<ParentLayout>({ direction: 'column', childSizing: 'auto' })

export function useWidthSetByParentRow(): boolean {
	const { direction, childSizing } = useContext(ParentLayoutContext)
	return direction === 'row' && childSizing === 'equal'
}

function flexStyle(
	direction: SubstackDirection,
	alignItems: SubstackAlignItems,
	justifyContent: SubstackJustifyContent,
): CSSProperties {
	return {
		display: 'flex',
		flexDirection: direction === 'row' ? 'row-reverse' : 'column-reverse',
		alignItems: alignItems === 'start' ? 'flex-start' : alignItems === 'end' ? 'flex-end' : alignItems,
		justifyContent: justifyContent === 'start'
			? 'flex-end'
			: justifyContent === 'end'
			? 'flex-start'
			: justifyContent,
	}
}

// Grid, not flex: flex adds each child's padding on top of its share, so padded and unpadded
// siblings would differ in size. Children arrive in paint order (the reverse of the layer
// tree), which grid has no `*-reverse` to undo, so the renderer flips them back.
// Row tracks may shrink below their content so widths stay equal and text wraps; column
// tracks keep a content minimum so nothing is clipped.
function equalSplitStyle(direction: SubstackDirection, alignItems: SubstackAlignItems): CSSProperties {
	return direction === 'row'
		? { display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'minmax(0, 1fr)', alignItems }
		: { display: 'grid', gridAutoFlow: 'row', gridAutoRows: '1fr', justifyItems: alignItems }
}

// A grid item with an explicit width/height doesn't stretch to its track.
const EQUAL_SPLIT_CHILD_CLASS: Record<SubstackDirection, string> = {
	row: '[&>[data-layer-id]]:!w-auto',
	column: '[&>[data-layer-id]]:!h-auto',
}

export type SubstackLayerRendererProps = {
	layerId: string
	direction: SubstackDirection
	alignItems: SubstackAlignItems
	justifyContent: SubstackJustifyContent
	childSizing: SubstackChildSizing
	widthSizing: SubstackWidthSizing
	gap: number
	fontFamilyMode: FontFamilyMode
	fontFamilyProvider: FontFamilyProvider
	fontFamilyGoogleFamily: string
	fontFamilyCustomUrl: string
	fontFamilyCustomName: string
	spacing: Spacing
	background: Background
	borderRadius: number
	borderRadiusCorners: BorderRadiusCorners
	children: ReactNode
}

export function SubstackLayerRenderer(
	{
		layerId,
		direction,
		alignItems,
		justifyContent,
		childSizing,
		widthSizing,
		gap,
		fontFamilyMode,
		fontFamilyProvider,
		fontFamilyGoogleFamily,
		fontFamilyCustomUrl,
		fontFamilyCustomName,
		spacing,
		background,
		borderRadius,
		borderRadiusCorners,
		children,
	}: SubstackLayerRendererProps,
) {
	const { style: fontFamilyStyle } = useFontFamilyStyle(
		fontFamilyMode,
		fontFamilyProvider,
		fontFamilyGoogleFamily,
		fontFamilyCustomUrl,
		fontFamilyCustomName,
	)

	const parentLayout = useContext(ParentLayoutContext)
	const layout = useMemo(() => ({ direction, childSizing }), [direction, childSizing])
	const fillStyle = widthSizing !== 'fill'
		? {}
		: parentLayout.direction === 'row'
		? { flexGrow: 1, minWidth: 0 }
		: parentLayout.childSizing === 'equal'
		? { justifySelf: 'stretch' }
		: { alignSelf: 'stretch' }

	return (
		<div
			data-layer-id={layerId}
			className={childSizing === 'equal'
				? `relative overflow-hidden ${EQUAL_SPLIT_CHILD_CLASS[direction]}`
				: 'relative overflow-hidden'}
			style={{
				pointerEvents: 'auto',
				...(childSizing === 'equal'
					? equalSplitStyle(direction, alignItems)
					: flexStyle(direction, alignItems, justifyContent)),
				...fillStyle,
				...fontFamilyStyle,
				...borderRadiusStyle(borderRadius, borderRadiusCorners),
				gap,
				paddingTop: spacing.paddingTop,
				paddingBottom: spacing.paddingBottom,
				paddingLeft: spacing.paddingLeft,
				paddingRight: spacing.paddingRight,
				marginTop: spacing.marginTop,
				marginBottom: spacing.marginBottom,
				marginLeft: spacing.marginLeft,
				marginRight: spacing.marginRight,
			}}
		>
			<BackgroundFill background={background} />
			<ParentLayoutContext value={layout}>
				{childSizing === 'equal' ? Children.toArray(children).reverse() : children}
			</ParentLayoutContext>
		</div>
	)
}
